import { Box } from '@chakra-ui/react';
import { CustomOnResize } from 'modules/Editor/components/BoundingBox';
import { ReplaceMenuTabIndex } from 'modules/Editor/store/editorSettingsSlice.types';
import { ReactElement, useCallback, useId } from 'react';
import {
  ReadOnlyWidgetBase,
  TransparentImg,
  WidgetBase,
  WidgetBaseProp,
  WidgetToolbar,
  useEditor,
  useWidget,
} from 'widgets/sdk';

import { generateLineStyle } from './LineIconWidget.helpers';
import { multiEditConfig } from './LineIconWidget.multiEdit';
import { LineIconLayoutProps, LineIconWidgetData } from './LineIconWidget.types';
import { LineIconWidgetToolbar } from './LineIconWidgetToolbar';
import { LINE_RESIZE_MAP, LINE_TYPE_MAP } from './Lines';

export const LineIconWidget = (): ReactElement => {
  const { type, widthPx, mirror, heightPx, strokeColor, strokeWidth, startArrowStyle, strokeDashType, endArrowStyle } =
    useWidget<LineIconWidgetData>();
  const id = useId();
  const { openReplaceWidgetMenu } = useEditor();

  const onDoubleClick = () => openReplaceWidgetMenu(ReplaceMenuTabIndex.ICON);

  const resizeLineIcon = useCallback(
    (widthPx: number, heightPx: number) => {
      if (LINE_RESIZE_MAP[type]) {
        LINE_RESIZE_MAP[type]({
          widthPx,
          heightPx,
          strokeWidth,
          startArrowStyle,
          endArrowStyle,
          id,
        });
      }
    },
    [strokeWidth, startArrowStyle, endArrowStyle, type, id],
  );

  const customOnResize = useCallback(
    ({ event, onResize }: CustomOnResize) => {
      onResize(event);

      const widthPx = event.target.clientWidth;
      const heightPx = event.target.clientHeight;

      // resize the line without setting the new dimensions to the state
      resizeLineIcon(widthPx, heightPx);
    },
    [resizeLineIcon],
  );

  return (
    <WidgetBase onResize={customOnResize} onDoubleClick={onDoubleClick}>
      <WidgetToolbar multiEditConfig={multiEditConfig}>
        <LineIconWidgetToolbar />
      </WidgetToolbar>
      <LineIconLayout
        widthPx={widthPx}
        heightPx={heightPx}
        strokeColor={strokeColor}
        startArrowStyle={startArrowStyle}
        strokeWidth={strokeWidth}
        strokeDashType={strokeDashType}
        endArrowStyle={endArrowStyle}
        id={id}
        type={type}
        mirror={mirror}
      />
    </WidgetBase>
  );
};

export const ReadOnlyLineIconWidget = ({ includeAltTextImg }: WidgetBaseProp): ReactElement => {
  const { type, widthPx, heightPx, mirror, strokeColor, strokeWidth, startArrowStyle, strokeDashType, endArrowStyle } =
    useWidget<LineIconWidgetData>();
  const id = useId();

  return (
    <ReadOnlyWidgetBase>
      <LineIconLayout
        widthPx={widthPx}
        heightPx={heightPx}
        strokeColor={strokeColor}
        startArrowStyle={startArrowStyle}
        strokeWidth={strokeWidth}
        strokeDashType={strokeDashType}
        endArrowStyle={endArrowStyle}
        id={id}
        type={type}
        mirror={mirror}
        isReadOnly
      />
      {includeAltTextImg && <TransparentImg />}
    </ReadOnlyWidgetBase>
  );
};

const LineIconLayout = ({
  widthPx,
  heightPx,
  id,
  strokeColor,
  strokeWidth,
  strokeDashType,
  startArrowStyle,
  endArrowStyle,
  type,
  mirror,
  isReadOnly,
}: LineIconLayoutProps) => {
  const Line = LINE_TYPE_MAP[type];
  const style = generateLineStyle(!!mirror?.isHorizontal);

  if (!Line) return <Box />;

  return (
    <Line
      widthPx={widthPx}
      heightPx={heightPx}
      strokeColor={strokeColor}
      startArrowStyle={startArrowStyle}
      strokeWidth={strokeWidth}
      strokeDashType={strokeDashType}
      endArrowStyle={endArrowStyle}
      id={id}
      style={style}
      isReadOnly={isReadOnly}
    />
  );
};
